import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation, Trans } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
    Text,
    Title,
    Link as UILink,
    List,
    ListItem,
    CartIcon,
    TruckIcon,
    PointerIcon,
} from '@paygreen/paygreen-ui';
import { ModalForm, Message } from '../../../../components';
import CarbonEquivalenceContent from './CarbonEquivalenceContent';
import DashboardCardTotalCo2 from '../DashboardCardTotalCo2';

const CarbonEquivalenceModal = ({ t, isOpen, carbonData }) => {
    const { convertedValue, convertedUnit } = carbonData;

    const co2ActivityContent = [
        {
            icon: <CartIcon />,
            details: (
                <Text textSize="xs" colorWab="grey50">
                    <Trans i18nKey="dashboard.equivalence.modal.activities.cartText">
                        ...
                        <Text htmlTag="strong" textSize="xs" colorWab="grey50">
                            {{
                                cart: t(
                                    'dashboard.equivalence.modal.activities.cart',
                                ),
                            }}
                        </Text>
                        ...
                    </Trans>

                    {/* cannot apply Trans component on <link> and <a> */}
                    <RouterLink to="/products">
                        <UILink
                            colorPallet="status"
                            colorStatus="success"
                            textSize="xs"
                        >
                            {t(
                                'dashboard.equivalence.modal.activities.linkProductsPage',
                            )}
                        </UILink>
                    </RouterLink>
                </Text>
            ),
        },
        {
            icon: <TruckIcon />,
            details: (
                <Text textSize="xs" colorWab="grey50">
                    <Trans i18nKey="dashboard.equivalence.modal.activities.deliveryText">
                        ...
                        <Text textSize="xs" htmlTag="strong" colorWab="grey50">
                            {{
                                delivery: t(
                                    'dashboard.equivalence.modal.activities.delivery',
                                ),
                            }}
                        </Text>
                        ...
                    </Trans>
                </Text>
            ),
        },
        {
            icon: <PointerIcon />,
            details: (
                <Text textSize="xs" colorWab="grey50">
                    <Trans i18nKey="dashboard.equivalence.modal.activities.navigationText">
                        ...
                        <Text textSize="xs" htmlTag="strong" colorWab="grey50">
                            {{
                                navigation: t(
                                    'dashboard.equivalence.modal.activities.navigation',
                                ),
                            }}
                        </Text>
                        ...
                    </Trans>
                </Text>
            ),
        },
    ];

    return (
        <ModalForm
            modalTitle={t('dashboard.equivalence.modal.title')}
            blockWidth="xl"
            paddingLateral="md"
            paddingBottom="md"
            hasCloseCross={true}
            isOpen={isOpen}
        >
            <Title htmlTag="h4" marginBottom="xs">
                {t('dashboard.equivalence.modal.subTitle1')}
            </Title>

            <Text textSize="xs" colorWab="grey50">
                <Trans i18nKey="dashboard.equivalence.modal.greenhouseGasText">
                    ...
                    <Text textSize="xs" htmlTag="strong" colorWab="grey50">
                        {{
                            greenhouseGas: t(
                                'dashboard.equivalence.modal.greenhouseGas',
                            ),
                        }}
                    </Text>
                    ...
                </Trans>
            </Text>

            <Text textSize="xs" marginTop="xs" colorWab="grey50">
                {t('dashboard.equivalence.modal.gasLifetime')}
            </Text>

            <Message type="success" paddingLateral="sm" marginTop="md">
                <Text textSize="xs" colorPallet="status" colorStatus="success">
                    <Trans i18nKey="dashboard.equivalence.modal.equivalenceText">
                        ...
                        <Text
                            textSize="xs"
                            colorPallet="status"
                            colorStatus="success"
                            htmlTag="strong"
                        >
                            {{
                                calculation: t(
                                    'dashboard.equivalence.modal.equivalence',
                                ),
                            }}
                        </Text>
                        ...
                    </Trans>
                </Text>
            </Message>

            <Title htmlTag="h4" marginTop="sm">
                {t('dashboard.equivalence.modal.subTitle2')}
            </Title>

            <Text textSize="xs" marginTop="xs" colorWab="grey50">
                {t('dashboard.equivalence.modal.co2CalculationIntro')}
            </Text>

            <List listStyle="icon" bulletSize="sm">
                {co2ActivityContent.map((element, index) => (
                    <ListItem
                        key={index}
                        bulletStyle="icon"
                        bulletSize="md"
                        marginTop={index === 0 ? 'sm' : 'none'}
                    >
                        {element.icon}

                        {element.details}
                    </ListItem>
                ))}
            </List>

            <Text textSize="xs" colorWab="grey50">
                {t('dashboard.equivalence.modal.co2CalculationEnd')}
            </Text>

            <Title htmlTag="h4" marginTop="sm" marginBottom="sm">
                {t('dashboard.equivalence.modal.subTitle3')}
            </Title>

            <DashboardCardTotalCo2
                carbonData={carbonData}
                hasDataLegend={false}
            />

            <CarbonEquivalenceContent
                carbonValue={convertedValue}
                unit={convertedUnit}
            />
        </ModalForm>
    );
};

CarbonEquivalenceModal.propTypes = {
    /**
     * @description isOpen is automatically received from ModalControl parent
     */
    isOpen: PropTypes.bool,
    carbonData: PropTypes.object,
};

CarbonEquivalenceModal.defaultProps = {
    isOpen: false,
};

export default withTranslation('common')(CarbonEquivalenceModal);
